import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { invokeToolAction } from '@/lib/tool-action';
import type {
  ContextualActionSpec,
  ToolActionConfirmationEdit,
  ToolActionRequest,
  ToolActionResponse,
} from '@shared/types/tool-actions';
import { ActionConnectionDialog } from './ActionConnectionDialog';
import { ToolActionConfirmationDialog } from './ToolActionConfirmationDialog';

/**
 * « Suivre » (decision « apps vivantes », A5): the user asks to add the company
 * of one result row to the CRM. The request only states the user's choice; the
 * existing contextual action plans it from the source run, shows a preview and
 * writes nothing before explicit confirmation. Once created, the HubSpot
 * company becomes a Domino account through the CRM sync.
 */
export const RowCrmAction = ({ sourceRunId, rowId, rowLabel, autoStart = false, onStarted, className = 'mt-3' }: {
  sourceRunId: string;
  /** The row of a list result; absent on the page of an account, where the run is about that company alone. */
  rowId?: string | null;
  rowLabel: string;
  /** Started from the table's action column: prepare the preview right away. */
  autoStart?: boolean;
  onStarted?: () => void;
  className?: string;
}) => {
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<{ tone: 'success' | 'error' | 'info'; text: string; url?: string | null } | null>(null);
  const [confirmation, setConfirmation] = useState<ToolActionResponse | null>(null);
  const [connection, setConnection] = useState<ToolActionResponse | null>(null);
  const runningRef = useRef(false);

  const actionLabel = `Ajouter ${rowLabel} au CRM`;
  const actionSpec: ContextualActionSpec = {
    version: 1,
    kind: 'contextual_action',
    source: 'selected_run',
    goal: rowId
      ? `Ajouter au CRM l’entreprise de la ligne « ${rowLabel} » de ce résultat, et elle seule, si elle n’y est pas déjà. Son nom dans le CRM est le nom de l’entreprise tel que la ligne l’écrit dans sa colonne entreprise, seul : jamais un intitulé d’offre, de poste ou de ligne. Joindre en note les informations de cette ligne qui expliquent pourquoi la suivre.`
      : `Ajouter au CRM l’entreprise « ${rowLabel} » dont traite ce résultat, et elle seule, si elle n’y est pas déjà, sous son seul nom d’entreprise. Joindre en note ce que ce résultat apprend d’elle.`,
    effects: [{ capabilityKey: 'crm.company.upsert' }, { capabilityKey: 'crm.note.create' }],
    target: rowId ? { kind: 'result_row', rowId, label: rowLabel } : null,
  };
  const request: ToolActionRequest = {
    actionKey: 'domino.apply_contextual_action',
    sourceRunId,
    sourceActionLabel: actionLabel,
    actionSpec,
  };

  const call = async (extra: Partial<ToolActionRequest> = {}) => {
    if (runningRef.current) return;
    runningRef.current = true;
    setRunning(true);
    setOutcome(null);
    const confirming = Boolean(extra.confirmationId);
    try {
      const response = await invokeToolAction({ ...request, ...extra });
      if (response.status === 'needs_confirmation' && response.confirmationId) {
        setConfirmation(response);
      } else if (response.status === 'needs_connection') {
        setConfirmation(null);
        setConnection(response);
      } else {
        // The dialog closes on every final answer so the result is visible.
        setConfirmation(null);
        const done = response.status === 'completed' || response.status === 'already_done';
        setOutcome({
          tone: done ? 'success' : response.status === 'partially_completed' ? 'info' : 'error',
          text: done && confirming ? `${rowLabel} est dans HubSpot. ${response.message}` : response.message,
          url: response.externalUrl ?? null,
        });
      }
    } catch (cause) {
      setConfirmation(null);
      setOutcome({
        tone: 'error',
        text: `${confirming ? 'Rien n’a été écrit dans HubSpot. ' : ''}${cause instanceof Error ? cause.message : 'Cette action n’a pas pu être préparée.'}`,
      });
    } finally {
      runningRef.current = false;
      setRunning(false);
    }
  };

  useEffect(() => {
    if (!autoStart) return;
    onStarted?.();
    void call();
    // Runs once per explicit start; call() guards against double submits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  const confirm = (candidateIds: string[], edits: ToolActionConfirmationEdit[]) =>
    void call({
      confirmationId: confirmation?.confirmationId ?? null,
      confirmedCandidateIds: candidateIds,
      confirmationEdits: edits,
    });

  return <div className={cn('flex flex-wrap items-center gap-3', className)}>
    <Button variant="secondary" size="sm" onClick={() => void call()} disabled={running}>
      {running ? 'Préparation…' : 'Ajouter au CRM'}
    </Button>
    {outcome && <p
      role={outcome.tone === 'error' ? 'alert' : 'status'}
      className={outcome.tone === 'error' ? 'text-sm text-red-700' : outcome.tone === 'success' ? 'text-sm text-emerald-700' : 'text-sm text-domino-inkSoft'}
    >
      {outcome.text}
      {outcome.url && <> <a className="underline" href={outcome.url} target="_blank" rel="noreferrer">Ouvrir dans HubSpot</a></>}
    </p>}
    <ToolActionConfirmationDialog
      response={confirmation}
      executing={running}
      onClose={() => setConfirmation(null)}
      onConfirm={confirm}
    />
    {connection && (connection.connectionRequirements?.length ?? 0) > 0 && (
      <ActionConnectionDialog
        actionLabel={actionLabel}
        message={connection.message}
        requirements={connection.connectionRequirements ?? []}
        onClose={() => setConnection(null)}
        onConnected={async () => { setConnection(null); await call(); }}
      />
    )}
  </div>;
};
